import { h, createContext } from 'preact';
import { useEffect, useState, useRef } from 'preact/hooks';

import Storefront from 'utils/storefront';
import * as queries from 'utils/storefront/queries';
import { decodeId } from 'utils/helpers';
import CartService, { CART_EVENTS } from './cart-service';

const Context = createContext();

const CartContext = ({ children, data }) => {
  const [cartState, setCartState] = useState(CartService.cart);
  const [productsData, setProductsData] = useState({});
  const [loading, setLoading] = useState(CartService.loading);
  const storefront = Storefront.getInstance();
  const loadedState = useRef(null);

  useEffect(() => {
    const listener = (event) => {
      setLoading(CartService.loading);
      setCartState(CartService.cart);
      if (event === CART_EVENTS.UPDATE) {
        document.dispatchEvent(new CustomEvent('openMiniCart'));
      }
    };
    listener('INIT');
    const getCart = () => {
      if (loadedState.current) {
        CartService.get();
      }
    };
    setTimeout(() => {
      loadedState.current = true;
    }, 1000);
    CartService.addListener(listener);
    window.addEventListener('pageshow', getCart);
    return () => {
      CartService.removeListener(listener);
      window.removeEventListener('pageshow', getCart);
    };
  }, []);

  useEffect(() => {
    if (cartState.item_count > 0) {
      const getProducts = async (ids) => {
        try {
          const response = await storefront.request({
            query: queries.getProductsByIds,
            variables: {
              ids,
            },
          });

          if (!response.data || !response.data.nodes) {
            // eslint-disable-next-line no-console
            console.error(
              'cart/cart-context.js: Storefront API request failed - no data or nodes',
              response,
            );
            return {};
          }

          // Filter out null/undefined products before processing
          const validProducts = response.data.nodes.filter(
            (item) => item !== null && item !== undefined && item.id,
          );

          return Object.fromEntries(
            validProducts.map((item) => [decodeId(item.id, 'Product'), item]),
          );
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('cart/cart-context.js: Error fetching products', error);
          return {};
        }
      };

      getProducts(
        cartState.items.map(
          (item) => `gid://shopify/Product/${item.product_id}`,
        ),
      ).then((items) => setProductsData(items));
    }
    document.dispatchEvent(
      new CustomEvent('updateCartCount', {
        detail: cartState.item_count,
      }),
    );
  }, [cartState]);

  /**
   * Wrapper for cart API to make interactions more simple
   */
  const cart = {
    add: (...params) => CartService.add(...params),
    remove: (...params) => CartService.remove(...params),
    update: (...params) => CartService.update(...params),
    state: cartState,
    productsData,
  };

  return (
    <Context.Provider value={{ cart, cartLoading: loading, data }}>
      {children}
    </Context.Provider>
  );
};

export const withCartContext = (Rendered) => (props) =>
  (
    <Context.Consumer>
      {(value) => <Rendered {...props} {...value} />}
    </Context.Consumer>
  );

export default CartContext;
